import { useMemo } from "react";
import { Activity, FinancialActivity } from "../../../api/services";
import { formatActivityGroupDate } from "../utils/activities";

type ActivityMonthGroups = {
  [month: string]: Activity[];
};

// Group activities with activityDttm by month
// Financial Activities with activityDttm = null are grouped by transaction status Pending/Authorising
export const createActivityGroupByMonth = (
  activities: Activity[],
): ActivityMonthGroups => {
  const groups: ActivityMonthGroups = {};
  activities.forEach((activity) => {
    let heading = "";
    if (!activity.activityDttm) {
      if ("transactionStatus" in activity) {
        const transaction = activity as FinancialActivity;
        if (transaction.transactionStatus === "Pending") {
          heading = "Pending";
        } else if (transaction.transactionStatus === "Authorising") {
          heading = "Authorising";
        } else {
          heading = "Unknown";
        }
      }
    } else {
      heading = formatActivityGroupDate(activity.activityDttm);
    }
    if (groups[heading] === undefined) {
      groups[heading] = [activity];
    } else {
      groups[heading].push(activity);
    }
  });
  return groups;
};

export const useActivityGroups = (activities: Activity[]) => {
  return useMemo(() => {
    const groups = createActivityGroupByMonth(activities);
    const headers = Object.keys(groups).sort((a, b) => {
      const otherHeaders = ["Authorising", "Pending"];
      const aIndex = otherHeaders.indexOf(a);
      const bIndex = otherHeaders.indexOf(b);
      // If either header is in the otherHeaders array, prioritise values later in the array
      if (aIndex || bIndex) {
        // Precedence(high to low): Pending(index 1) -> Authorising(index 0) -> not in array(index -1)
        return aIndex === bIndex ? 0 : aIndex < bIndex ? 1 : -1;
      }
      // Both headers are months
      return new Date(b).getTime() - new Date(a).getTime();
    });
    return { groups, headers };
  }, [activities]);
};

export const isPending = (header: string) =>
  header === "Pending" || header === "Authorising";
